import posthog, { CapturedNetworkRequest } from "posthog-js";

import { sanitizeWaitlistLinkForAnalytics } from "@/lib/booking-link-privacy";
import { wasExceptionHandled } from "@/lib/posthog-error-dedup";
import { installTranslationDomSafety } from "@/lib/translation-dom-safety";

export function getPublicEnv(
  key: "NEXT_PUBLIC_POSTHOG_KEY" | "NEXT_PUBLIC_POSTHOG_HOST",
): string {
  if (typeof window !== "undefined" && (window as any).RUNTIME_ENV) {
    return (window as any).RUNTIME_ENV[key] ?? "";
  }
  return process.env[key] ?? "";
}

function initPosthog() {
  const key = getPublicEnv("NEXT_PUBLIC_POSTHOG_KEY");
  const host = getPublicEnv("NEXT_PUBLIC_POSTHOG_HOST");

  if (!key || !host) {
    console.warn(
      "PostHog not initialized: missing NEXT_PUBLIC_POSTHOG_KEY or NEXT_PUBLIC_POSTHOG_HOST",
    );
    return;
  }

  posthog.init(key, {
    api_host: host,
    // Keep rrweb capture running so PostHog can buffer every console session.
    // Sampling is only overridden from explicit user workflows below.
    disable_session_recording: false,
    session_recording: {
      maskCapturedNetworkRequestFn: (data: CapturedNetworkRequest) => {
        return sanitizeWaitlistLinkForAnalytics(data);
      },
    },
    before_send: (event) => {
      if (!event) {
        return null;
      }

      if (
        event.event === "$exception" &&
        !event.properties?.$ky_error &&
        wasExceptionHandled(event)
      ) {
        return null;
      }

      const pathname = window.location.pathname;
      // URL pathnames use hyphens (`acme-clinic`) but the Postgres
      // `all_*.tenant` column carries the schema name (`acme_clinic`).
      // Normalize on capture so PostHog and Postgres tenant values are
      // directly comparable, and so the `dev_` global tenant exclusion
      // in `decoda/internal/analytics/tenant_exclusions.py` actually
      // matches `dev_*` events.
      const tenant = pathname.split("/")[1]?.replaceAll("-", "_");

      if (tenant) {
        event.properties = {
          ...event.properties,
          tenant,
        };
      }

      return sanitizeWaitlistLinkForAnalytics(event);
    },
  });

  posthog.startSessionRecording();

  window.posthog = posthog;
}

if (typeof window !== "undefined") {
  installTranslationDomSafety();
  if ((window as any).RUNTIME_ENV) initPosthog();
  else window.addEventListener("load", initPosthog, { once: true });
}

export default posthog;
